/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    Button,
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    Node,
    TelemetryConstant,
    Modal,
    IModalProps,
    ModalHeader,
    ModalBody,
    ModalFooter
} from '@msdyn365-commerce-modules/utilities';
import {
    ChannelDeliveryOptionConfiguration,
    ChannelIdentity,
    Customer,
    OrderOriginator,
    OrgUnitLocation,
    SalesOrder,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Observer } from 'mobx-react';
import React from 'react';

import classnames from 'classnames';

import { getGroups, IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { getOrderInformation, getOrderInformationInput, IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { getSalesLines, getSalesLinesInput, IGetSalesLinesInput } from '@msdyn365-commerce-modules/order-management';
import { ExpandButtonState, IPriceContext, SalesLineState } from '@msdyn365-commerce-modules/order-management';
import {
    INssCOrderHistoryWithSearchAndFiltersProps,
    INssCOrderHistoryWithSearchAndFiltersResources
} from '../nss-c-order-history-with-search-and-filters.props.autogenerated';
import { INssCOrderHistoryWithSearchAndFiltersData } from '../nss-c-order-history-with-search-and-filters.data';
import { getOrderDetailsPageUrl } from '../common/get-sales-order';
import { SalesOrderExtended } from '../actions/get-nss-c-order-history-with-search-and-filters';
import { nSS_UpdateSalesOrderApprovalStatusAsync } from '../../../proxy/DataActionExtension.g';
import { OrderStatusStateShareInput, IOrderStatus } from '../../../actions/order-status-state-share.action';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-order-list';
const orderListItemClassName = `${componentClassName}__sales-order`;
const orderInformationHeaderClassName = `${componentClassName}__sales-order__header`;
const orderInformationCountClassName = `${componentClassName}__sales-order__order-number-count`;
const expandProductsButtonClassName = `${componentClassName}__sales-order__expand-products-button`;
const expandProductsButtonWrapperClassName = `${componentClassName}__sales-order__expand-products-button-wrapper`;
const orderDetailButtonClassName = `${componentClassName}__btn-order-details`;
const moduleName = componentClassName;

/**
 * IOrderHistoryWithSearchAndFiltersOrderListData interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderListData {
    salesOrders: SalesOrderExtended[];
    products: SimpleProduct[];
    channelIdentities?: ChannelIdentity[];
    orgUnitLocations?: OrgUnitLocation[];
    customer?: Customer;
    originators?: OrderOriginator[];
}

export interface IExtendedOrderInformation extends IOrderInformation {
    orderStatus?: React.ReactNode;
}

/**
 * IOrderHistoryWithSearchAndFiltersOrderListProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderListProps extends IComponentProps<IOrderHistoryWithSearchAndFiltersOrderListData> {
    moduleProps: INssCOrderHistoryWithSearchAndFiltersProps<INssCOrderHistoryWithSearchAndFiltersData>;
    className?: string;
    resources: INssCOrderHistoryWithSearchAndFiltersResources;
    isMobileView?: boolean;
    showChannelInfo: boolean;
    showOrderPlacedBy?: boolean;
    salesLinesLimit?: number;
    telemetryContent?: ITelemetryContent;
    retailMultiplePickUpOptionEnabled?: boolean;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
}

/**
 * IOrderHistoryWithSearchAndFiltersOrderItemProps interface.
 */
interface IOrderHistoryWithSearchAndFiltersOrderItemProps {
    orderInfomation: IExtendedOrderInformation;
    groups?: IGroups;
    orderDetailsLink: React.ReactNode;
    expandProductsButton?: React.ReactNode;
}

export const getOrderApprovalStatus = (props: ISalesOrderApproval): React.ReactNode => {
    const {
        salesOrder,
        context,
        data: { customer },
        moduleProps: {
            data: { orderStatus }
        }
    } = props;
    const { saleOrderApprovalStatusAttributeName, customerSaleOrderApprovalRequiredAttributeName } = context.app.config;
    let approvalStatus: string | undefined = '';
    let showApprovalStatus: boolean = false;
    const updatedApprovalStatus = orderStatus.result?.orderStatus.find(orderId => orderId.saleOrderId === salesOrder.SalesId)?.orderStatus;
    if (customer && salesOrder) {
        const customerApproval = customer.Attributes?.find(attr => attr.Name === customerSaleOrderApprovalRequiredAttributeName);
        const orderStatusAttribute = salesOrder.Attributes?.find(attr => attr.Name === saleOrderApprovalStatusAttributeName);
        if (salesOrder.SalesId && orderStatusAttribute && customerApproval?.AttributeValue?.StringValue === 'Yes') {
            showApprovalStatus = true;
            approvalStatus = orderStatusAttribute.TextValue?.replace('-', ' ');
        }
    }
    if (showApprovalStatus) {
        return (
            <span className='ms-order-history__order-information-channel-reference-id order-status'>
                Approval Status: <span>{updatedApprovalStatus ? updatedApprovalStatus : approvalStatus}</span>
            </span>
        );
    }
    return null;
};

/**
 * OrderInfomation component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderInfomationComponent: React.FC<IExtendedOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    channelName,
    createdDate,
    count,
    amount,
    channelReferenceId,
    orderStatus
}) => (
    <Node {...orderInformationProps}>
        <div className={orderInformationHeaderClassName}>{channelName}</div>
        <div className={orderInformationCountClassName}>
            {salesId}
            {count}
        </div>
        {receiptId}
        {createdDate}
        {amount}
        {channelReferenceId}
        {orderStatus}
    </Node>
);

/**
 * GroupDelivery component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const GroupDeliveryComponent: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, processing, address, trackingInfo }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

/**
 * Group component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const GroupComponent: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <GroupDeliveryComponent {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

/**
 * Groups component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const GroupsComponent: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <GroupComponent key={index} {...group} />
        ))}
    </Node>
);

/**
 * Updates product states to show all products.
 * @param expandButtonState - Expand button state.
 * @param productStates - Product states.
 * @returns Callback method.
 */
const onExpandProductsCallback = (expandButtonState: ExpandButtonState, productStates: SalesLineState[]) => () => {
    for (const productState of productStates) {
        productState.isHidden = false;
    }
    expandButtonState.extraProductsCount = 0;
};

/**
 * Gets the IOrderHistoryWithSearchAndFiltersOrderItem configuration.
 * @param salesOrder The sales order
 * @param index The sales order index
 * @param props The component props
 * @returns The IOrderHistoryWithSearchAndFiltersOrderItem configuration.
 */
const createOrderHistoryWithSearchAndFiltersOrderItemProps = (
    salesOrder: SalesOrderExtended,
    index: number,
    props: IOrderHistoryWithSearchAndFiltersOrderListProps
): IOrderHistoryWithSearchAndFiltersOrderItemProps => {
    const {
        id,
        typeName,
        context,
        moduleProps: { telemetry },
        data: { products, channelIdentities, orgUnitLocations, customer, originators },
        showChannelInfo,
        telemetryContent,
        resources: { freePriceText, detailsAriaLabel, detailsLabel, orderItemLabel, orderItemsLabel },
        context: { actionContext },
        retailMultiplePickUpOptionEnabled,
        channelDeliveryOptionConfig
    } = props;

    const priceContext = {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    } as IPriceContext;

    const originator: OrderOriginator | undefined = originators?.find(
        originator =>
            (salesOrder.SalesId && originator.SalesId === salesOrder.SalesId) ||
            (salesOrder.Id && originator.TransactionId === salesOrder.Id)
    );

    const salesLinesInput =
        products &&
        getSalesLinesInput(
            props.moduleProps,
            moduleName,
            salesOrder,
            products,
            priceContext,
            undefined,
            undefined,
            undefined,
            retailMultiplePickUpOptionEnabled,
            channelDeliveryOptionConfig
        );

    let salesLines: ISalesLine[] | undefined;
    let expandProductsButton: React.ReactNode;
    if (salesLinesInput) {
        const getSalesLinesInputProps: IGetSalesLinesInput = { ...salesLinesInput, imageOnly: true };
        if (!ObjectExtensions.isNullOrUndefined(props.salesLinesLimit)) {
            const extraProductsCount = getSalesLinesInputProps.salesLines.length - props.salesLinesLimit;
            getSalesLinesInputProps.salesLineStates = getSalesLinesInputProps.salesLines.map(
                (salesLine, index) => new SalesLineState(`${salesLine.LineId ?? ''}-${index}`, index >= props.salesLinesLimit!)
            );
            if (extraProductsCount > 0) {
                const expandButtonState = new ExpandButtonState(extraProductsCount);
                expandProductsButton = (
                    <>
                        <Observer>
                            {() => {
                                const text = format(
                                    props.resources.orderHistoryExpandProductsButtonText,
                                    expandButtonState.extraProductsCount
                                );
                                return (
                                    expandButtonState.extraProductsCount > 0 && (
                                        <Button
                                            className={expandProductsButtonClassName}
                                            title={text}
                                            role='button'
                                            onClick={onExpandProductsCallback(expandButtonState, getSalesLinesInputProps.salesLineStates!)}
                                            aria-label={text}
                                        >
                                            {text}
                                        </Button>
                                    )
                                );
                            }}
                        </Observer>
                    </>
                );
            }
        }
        salesLines = getSalesLines(getSalesLinesInputProps);
    }

    expandProductsButton = (
        <>
            {expandProductsButton}
            <div className={expandProductsButtonWrapperClassName} />
        </>
    );

    const groups =
        salesLines &&
        getGroups({
            moduleName,
            salesLines,
            resources: { orderItemLabel, orderItemsLabel },
            retailMultiplePickUpOptionEnabled
        });

    const orderInfomationInput = getOrderInformationInput(
        props.moduleProps,
        moduleName,
        salesOrder,
        priceContext,
        channelIdentities,
        orgUnitLocations,
        customer,
        originator,
        showChannelInfo
    );

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.OrderDetails);
    const orderDetailsAttributes = getTelemetryAttributes(telemetryContent!, payLoad);

    const orderDetailsLink = (
        <div className='order-approval'>
            <SalesOrderApproval {...props} salesOrder={salesOrder} />
            <Button
                href={getOrderDetailsPageUrl(actionContext, salesOrder)}
                role='button'
                aria-label={
                    salesOrder.ChannelReferenceId && detailsAriaLabel
                        ? detailsAriaLabel.replace('{orderId}', salesOrder.ChannelReferenceId)
                        : ''
                }
                className={orderDetailButtonClassName}
                {...orderDetailsAttributes}
            >
                {detailsLabel}
            </Button>
        </div>
    );
    const orderInformation = getOrderInformation(orderInfomationInput);
    const newOrderInformation: IExtendedOrderInformation = {
        ...orderInformation,
        orderStatus: getOrderApprovalStatus({ ...props, salesOrder }) // Adding a new property 'orderStatus' with the new React Element
    };
    return {
        orderInfomation: newOrderInformation,
        groups: groups,
        orderDetailsLink: orderDetailsLink,
        expandProductsButton: expandProductsButton
    };
};

interface ISalesOrderApproval extends IOrderHistoryWithSearchAndFiltersOrderListProps {
    salesOrder: SalesOrderExtended;
}

const SalesOrderApproval = (props: ISalesOrderApproval) => {
    const {
        salesOrder,
        context: { actionContext, app },
        data: { customer },
        moduleProps: {
            data: { orderStatus }
        }
    } = props;

    const [showApprovalStatus, setShowApprovalStatus] = React.useState(false);
    const [updateApprovalStatusAllowed, setUpdateApprovalStatusAllowed] = React.useState(true);
    const [approvalStatus, setApprovalStatus] = React.useState<string | undefined>('Not Approved');
    const [isApproved, setIsApproved] = React.useState(false);
    const [rejectionComments, setRejectionComments] = React.useState('');
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [modalHasError, setModalHasError] = React.useState(false);
    const { reasonForRejection, errorForRejectionReason, btnSubmitLabel, approveLabel, rejectLabel } = props.resources;

    React.useEffect(() => {
        const { saleOrderApprovalStatusAttributeName, customerSaleOrderApprovalRequiredAttributeName } = app.config;

        if (customer && salesOrder) {
            const customerApproval = customer.Attributes?.find(attr => attr.Name === customerSaleOrderApprovalRequiredAttributeName);
            const orderStatusAttribute = salesOrder.Attributes?.find(attr => attr.Name === saleOrderApprovalStatusAttributeName);
            if (
                salesOrder.SalesId &&
                orderStatusAttribute &&
                customer.IsB2bAdmin &&
                customerApproval?.AttributeValue?.StringValue === 'Yes'
            ) {
                setShowApprovalStatus(true);
                if (orderStatusAttribute.TextValue === 'Approved') {
                    setApprovalStatus(orderStatusAttribute.TextValue);
                    setIsApproved(true);
                    setUpdateApprovalStatusAllowed(false);
                } else if (orderStatusAttribute.TextValue === 'Pending') {
                    setUpdateApprovalStatusAllowed(true);
                } else {
                    setApprovalStatus(orderStatusAttribute.TextValue?.replace('-', ' '));
                    setUpdateApprovalStatusAllowed(false);
                }
            }
        }
    }, []);

    const updateApprovalStatus = async () => {
        setIsApproved(true);
        setApprovalStatus('Approved');
        setUpdateApprovalStatusAllowed(false);
        void nSS_UpdateSalesOrderApprovalStatusAsync({ callerContext: actionContext }, salesOrder?.SalesId || '', 'Approved', '');
        const orderStatuses: IOrderStatus[] = orderStatus.result?.orderStatus ?? [];
        const orderStatusData: IOrderStatus = {
            orderStatus: 'Approved',
            saleOrderId: salesOrder.SalesId ? salesOrder.SalesId : ''
        };
        const replacedIndex = orderStatuses.findIndex(orderStatus => orderStatus.saleOrderId === salesOrder.SalesId);
        if (replacedIndex >= 0) {
            orderStatuses[replacedIndex] = orderStatusData;
        } else {
            orderStatuses.push(orderStatusData);
        }

        actionContext.update(new OrderStatusStateShareInput(), { orderStatus: orderStatuses });
    };

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
        setModalHasError(false);
        const comments = MsDyn365.isBrowser && (document.getElementById('rejectionReason') as HTMLInputElement);
        if (comments) {
            comments.value = '';
        }
    };
    const modalPropsTerms: IModalProps = {
        applicationNode: MsDyn365.isBrowser ? document.body : '',
        isOpen: isModalOpen,
        autoFocus: true,
        size: 'sm',
        keyboard: true,
        backdrop: true,
        zIndex: 1000,
        fade: true,
        className: 'msc-modal__order-status',
        toggle: toggleModal
    };
    const addRejectionComments = () => {
        if (rejectionComments) {
            setIsApproved(false);
            setApprovalStatus('Not Approved');
            setUpdateApprovalStatusAllowed(false);
            void nSS_UpdateSalesOrderApprovalStatusAsync(
                { callerContext: actionContext },
                salesOrder?.SalesId || '',
                'Not-Approved',
                rejectionComments
            );
            const orderStatuses: IOrderStatus[] = orderStatus.result?.orderStatus ?? [];
            const orderStatusData: IOrderStatus = {
                orderStatus: 'Not Approved',
                saleOrderId: salesOrder.SalesId ? salesOrder.SalesId : ''
            };
            orderStatuses.push(orderStatusData);
            actionContext.update(new OrderStatusStateShareInput(), { orderStatus: orderStatuses });
            setIsModalOpen(false);
            setModalHasError(false);
        } else {
            setModalHasError(true);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRejectionComments(event.target.value);
        if (event.target.value === '') {
            setModalHasError(true);
        } else {
            setModalHasError(false);
        }
    };

    const statusClass = classnames('approval-status', { approved: isApproved, 'not-approved': !isApproved });
    return showApprovalStatus ? (
        <div className={statusClass}>
            <label htmlFor='approval-status'>{approvalStatus}</label>
            <div className='approval-status-cta'>
                <button className='approve' onClick={updateApprovalStatus} disabled={updateApprovalStatusAllowed ? false : true}>
                    {approveLabel}
                </button>
                <button className='reject' onClick={toggleModal} disabled={updateApprovalStatusAllowed ? false : true}>
                    {rejectLabel}
                </button>
            </div>
            <Modal {...modalPropsTerms}>
                <ModalHeader closeAriaLabel='Close' closeButtonLabel='Close' toggle={toggleModal}>
                    <span>{reasonForRejection}</span>
                </ModalHeader>
                <ModalBody>
                    <div>
                        <textarea
                            id='rejectionReason'
                            rows={4}
                            cols={50}
                            placeholder={'Type your rejection reason...'}
                            onChange={handleChange}
                            value={rejectionComments}
                        ></textarea>
                    </div>
                    {modalHasError && (
                        <div className='rejection-comment-error'>
                            <span>{errorForRejectionReason}</span>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={addRejectionComments}
                        disabled={modalHasError ? true : false}
                        className='ms-order-history__btn-order-details msc-btn'
                    >
                        {btnSubmitLabel}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    ) : null;
};

/**
 * OrderHistoryWithSearchAndFiltersOrderList component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersOrderListFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersOrderListProps> = (
    props: IOrderHistoryWithSearchAndFiltersOrderListProps
) => {
    return (
        <Node className={classnames(props.className, componentClassName)}>
            {ArrayExtensions.hasElements(props.data.salesOrders) &&
                props.data.salesOrders.map((salesOrder: SalesOrder, index: number) => {
                    const itemProps: IOrderHistoryWithSearchAndFiltersOrderItemProps = createOrderHistoryWithSearchAndFiltersOrderItemProps(
                        salesOrder,
                        index,
                        props
                    );
                    return (
                        <Node className={orderListItemClassName} key={salesOrder.SalesId || salesOrder.Id}>
                            {itemProps.orderInfomation && <OrderInfomationComponent {...itemProps.orderInfomation} />}
                            {itemProps.groups && <GroupsComponent {...itemProps.groups} />}
                            {itemProps.expandProductsButton}
                            {itemProps.orderDetailsLink}
                        </Node>
                    );
                })}
        </Node>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersOrderList component.
 */
export const OrderHistoryWithSearchAndFiltersOrderListComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersOrderListProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersOrderListProps
>('OrderHistoryWithSearchAndFiltersOrderListComponent', { component: OrderHistoryWithSearchAndFiltersOrderListFunctionalComponent });
